/**
 * הצגת תאריכים בלוח העברי (גימטריה, ללא ניקוד).
 * דוגמה: כ״ב בתשרי תשפ״ז
 * עם weekday: יום שלישי, כ״ב בתשרי תשפ״ז
 * עם זמן: כ״ב בתשרי תשפ״ז, 19:45
 */
import { HDate } from '@hebcal/hdate';

const HEBREW_WEEKDAYS = [
  'יום ראשון',
  'יום שני',
  'יום שלישי',
  'יום רביעי',
  'יום חמישי',
  'יום שישי',
  'יום שבת',
];

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * מפרסר ערך תאריך כך ש־YYYY-MM-DD ייחשב כיום אזרחי מקומי (לא UTC).
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseDisplayDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (DATE_ONLY_RE.test(trimmed)) {
      const [y, m, d] = trimmed.split('-').map(Number);
      const local = new Date(y, m - 1, d);
      return Number.isNaN(local.getTime()) ? null : local;
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {string|number|Date|null|undefined} value
 * @param {{ weekday?: boolean, year?: boolean }} [options]
 * @returns {string}
 */
export function formatHebrewDate(value, options = {}) {
  const d = parseDisplayDate(value);
  if (!d) return '';
  try {
    const hd = new HDate(d);
    // renderGematriya(true) → "ט״ו חשון תשס״ט" / "י״ד אדר ב׳ תשפ״ד"
    const raw = hd.renderGematriya(true);
    const parts = String(raw).trim().split(/\s+/).filter(Boolean);
    let datePart = raw;
    if (parts.length >= 3) {
      const day = parts[0];
      const year = parts[parts.length - 1];
      const month = parts.slice(1, -1).join(' ');
      datePart = options.year === false
        ? `${day} ב${month}`
        : `${day} ב${month} ${year}`;
    }
    if (options.weekday) {
      return `${HEBREW_WEEKDAYS[d.getDay()]}, ${datePart}`;
    }
    return datePart;
  } catch {
    return '';
  }
}

/**
 * תאריך עברי + שעה מקומית (HH:MM).
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function formatHebrewDateTime(value) {
  const d = parseDisplayDate(value);
  if (!d) return '';
  const datePart = formatHebrewDate(d);
  if (!datePart) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${datePart}, ${hh}:${mm}`;
}

/**
 * גרסה עם שניות — ללוגי / סנכרון.
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function formatHebrewDateTimeSeconds(value) {
  const d = parseDisplayDate(value);
  if (!d) return '';
  const datePart = formatHebrewDate(d);
  if (!datePart) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${datePart}, ${hh}:${mm}:${ss}`;
}
